import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../context/AuthContext';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { CrossPortalEnrollmentModal } from './CrossPortalEnrollmentModal';

const loginSchema = z.object({
  email: z.string().email('Voer een geldig e-mailadres in'),
  password: z.string().min(6, 'Wachtwoord moet minimaal 6 tekens bevatten'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess?: () => void;
}

/**
 * Validate returnTo param to prevent open redirect attacks.
 * Only accepts relative paths starting with / (rejects absolute URLs and protocol-relative paths).
 */
function getReturnToDestination(searchParams: URLSearchParams): string {
  const returnTo = searchParams.get('returnTo');
  if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
    return returnTo;
  }
  return '/recent';
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn, error, clearError } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const destination = getReturnToDestination(searchParams);

  // Pre-fill email if coming from registration with existing account
  const prefilledEmail = (location.state as { email?: string })?.email;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: prefilledEmail || '',
    },
  });

  // Clear error when redirected from registration (prefilled email means redirect)
  useEffect(() => {
    if (prefilledEmail) {
      clearError();
    }
  }, [prefilledEmail, clearError]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsSubmitting(true);
      clearError();
      const result = await signIn(data.email, data.password);

      // Don't redirect if enrollment is needed - modal will handle that
      if (result.needsEnrollment) {
        return;
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate(destination);
      }
    } catch (_err) {
      // Error is already set in context
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CrossPortalEnrollmentModal returnTo={destination} />

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Inloggen</CardTitle>
          <CardDescription>
            Log in om toegang te krijgen tot de jurisprudentie database
          </CardDescription>
        </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">E-mailadres</Label>
            <Input
              id="email"
              type="email"
              placeholder="naam@voorbeeld.nl"
              {...register('email')}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Wachtwoord</Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              disabled={isSubmitting}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <div className="text-sm">
            <Link 
              to="/forgot-password" 
              className="text-primary hover:underline"
            >
              Wachtwoord vergeten?
            </Link>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4 pt-6">
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Bezig met inloggen...' : 'Inloggen'}
          </Button>
          
          <p className="text-sm text-muted-foreground text-center">
            Nog geen account?{' '}
            <Link 
              to="/register" 
              className="text-primary hover:underline"
            >
              Registreer hier
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
    </>
  );
}